import { useState, useEffect, useRef } from "react";
import API_BASE_URL from "../config";
import useActiveJobId from "../hooks/useActiveJobId";
import useDocumentStatusStream from "../hooks/useDocumentStatusStream";
import useJobWaiter from "../hooks/useJobWaiter";
import { LoaderCircle } from "lucide-react";
import { MatchingResults, SummaryBar, LVItemsTable, MATCHING_TAB } from "./matching";
import "./LVWorkflow.css";

const ICON_SIZE = 14;

const DEFAULT_DATABASE = "Betonbau";
// Remembers the in-progress upload so returning to this view resumes the progress
// view (the work continues server-side regardless).
const ACTIVE_UPLOAD_KEY = "lvActiveUpload";
const STEP5_ADVANCE_DELAY_MS = 500;
const STEP6_ADVANCE_DELAY_MS = 400;
const APPROVE_RESET_DELAY_MS = 1500;
const MAX_UPLOAD_BYTES = 50 * 1024 * 1024;
const RUN_NOT_RECOVERABLE_MESSAGE =
  "The previous matching run could not be restored. Start over to match this LV again.";

const STEPS = [
  { id: 1, label: "Supervisor — LV classification via keyword detection" },
  { id: 2, label: "Extraction — structured parsing" },
  { id: 3, label: "Filter — Ziegel & Betonfertigteile extracted" },
  { id: 4, label: "Product Matcher — spec comparison vs catalog" },
  { id: 5, label: "Graph Enrichment — normative values from knowledge graph" },
  { id: 6, label: "Reflection — confidence check, missing data flagged" },
  { id: 7, label: "Human Review (mandatory)" },
];

// How many steps are *completed* (not active) per phase
const COMPLETED_THROUGH = {
  idle: 0,
  uploading: 0,
  processing: 1,
  extracting: 2,
  matching: 3,
  review: 6,
  failed: 0,
};

// Which step is currently *active* per phase (null = none)
const ACTIVE_STEP = {
  idle: null,
  uploading: 1,
  processing: 2,
  extracting: 3,
  matching: 4,
  review: 7,
  failed: null,
};

export default function LVWorkflow({ database, onStatusChange }) {
  const { waitFor } = useJobWaiter();
  const [phase, setPhase] = useState("idle");
  const [file, setFile] = useState(null);
  const [error, setError] = useState(null);
  const [docId, setDocId] = useState(null);
  const [docFilename, setDocFilename] = useState("");
  const [statusMsg, setStatusMsg] = useState("");
  const [lvCount, setLvCount] = useState(null);
  const [matchStep, setMatchStep] = useState(4);

  const [catalogs, setCatalogs] = useState([]);
  const [catalogId, setCatalogId] = useState(null);
  const [catalogLabel, setCatalogLabel] = useState("");

  const [report, setReport] = useState(null);
  const [activeTab, setActiveTab] = useState(MATCHING_TAB.SUPPLY);
  const [corrections, setCorrections] = useState({});
  const [editingRow, setEditingRow] = useState(null);
  const [catalogProducts, setCatalogProducts] = useState([]);
  const [catalogProductsError, setCatalogProductsError] = useState(false);
  const [documentUrl, setDocumentUrl] = useState("");
  const [documentName, setDocumentName] = useState("");
  const [lvItems, setLvItems] = useState([]);
  const [exportBusy, setExportBusy] = useState(false);

  // Refs so async effects always see current values without re-triggering
  const catalogIdRef = useRef(null);
  const databaseRef = useRef(database);
  // Persist the whole resumable run (not just the upload) so returning mid-pipeline
  // re-attaches instead of resetting to idle. matchingJobIdRef lets a review-phase
  // return re-attach to the finished job instead of re-running the matcher.
  const [savedActive, saveActive, clearActive] = useActiveJobId(ACTIVE_UPLOAD_KEY);
  const matchingJobIdRef = useRef(null);
  const reattachMatchingJobIdRef = useRef(null);
  // True when the matching phase was entered by RESUMING a saved run (its
  // matcher was already started on an earlier visit), as opposed to arriving
  // there from extraction. A resumed run may only re-attach to its saved job —
  // never POST a new one, which would silently launch a duplicate LLM run.
  const resumedMatchingRef = useRef(false);

  useEffect(() => { catalogIdRef.current = catalogId; }, [catalogId]);
  useEffect(() => { databaseRef.current = database; }, [database]);

  // Load catalogs once on mount / database change
  useEffect(() => {
    const qs = database ? `?database=${encodeURIComponent(database)}` : "";
    fetch(`${API_BASE_URL}/api/v1/product-catalog/documents${qs}`)
      .then((r) => r.json())
      .then((data) => {
        const docs = data.documents || [];
        setCatalogs(docs);
        // Default to the first catalog only when none is chosen yet — don't clobber a
        // reattached run's saved catalog (or a manual selection).
        if (docs.length >= 1 && !catalogIdRef.current) {
          const d = docs[0];
          setCatalogId(d.document_id);
          catalogIdRef.current = d.document_id;
          setCatalogLabel(d.filename || d.name || "");
        }
      })
      .catch(() => setError("Failed to load product catalogs. Please refresh."));
  }, [database]);

  // Resume an in-progress run after returning to this view: the work runs
  // server-side, so re-attach at the saved phase and keep showing progress.
  const reattachedRef = useRef(false);
  useEffect(() => {
    if (reattachedRef.current || !savedActive?.docId) return;
    reattachedRef.current = true;
    setDocId(savedActive.docId);
    setDocFilename(savedActive.filename || "");
    if (savedActive.catalogId) {
      setCatalogId(savedActive.catalogId);
      catalogIdRef.current = savedActive.catalogId;
    }
    const p = savedActive.phase || "processing";
    if (p === "matching" || p === "review") {
      // Re-attach to the matching job (finished or running) instead of re-running it.
      reattachMatchingJobIdRef.current = savedActive.matchingJobId || null;
      resumedMatchingRef.current = true;
      setPhase("matching");
    } else if (p === "extracting") {
      setPhase("extracting");
    } else {
      setStatusMsg("Verarbeitung läuft…");
      setPhase("processing"); // the polling effect re-attaches and advances on completion
    }
  }, [savedActive]);

  // Persist the resumable state as the pipeline advances; clear on failure (idle is
  // cleared by reset()). The catalog + matching job id ride along so a later return
  // re-attaches to the exact run.
  useEffect(() => {
    if (["processing", "extracting", "matching", "review"].includes(phase) && docId) {
      saveActive({
        docId,
        filename: docFilename,
        phase,
        catalogId: catalogIdRef.current || undefined,
        matchingJobId: matchingJobIdRef.current || undefined,
      });
    } else if (phase === "failed") {
      clearActive();
    }
  }, [phase, docId, docFilename, saveActive, clearActive]);

  // Notify parent card of phase changes
  useEffect(() => {
    if (!onStatusChange) return;
    if (phase === "idle" || phase === "failed") {
      onStatusChange("idle", "Idle");
      return;
    }
    const step =
      phase === "matching"
        ? matchStep
        : ACTIVE_STEP[phase] ?? COMPLETED_THROUGH[phase] + 1;
    onStatusChange("running", `Running · step ${step}/7`);
  }, [phase, matchStep, onStatusChange]);

  // Document processing status — SSE primary, safety-net poll fallback (see
  // useDocumentStatusStream), active only during "processing".
  const { status: docStatus } = useDocumentStatusStream(docId, {
    enabled: phase === "processing",
  });
  useEffect(() => {
    if (phase !== "processing" || !docStatus) return;
    if (docStatus.message) setStatusMsg(docStatus.message); // live step text (OCR / graph…)
    if (docStatus.status === "completed") {
      setStatusMsg("");
      setPhase("extracting"); // persist effect updates the saved phase
    } else if (docStatus.status === "failed") {
      setError(docStatus.message || "Processing failed");
      setPhase("failed"); // persist effect clears the saved state
    }
  }, [phase, docStatus]);

  // Extraction effect — fires once when phase becomes "extracting"
  useEffect(() => {
    if (phase !== "extracting" || !docId) return;
    const controller = new AbortController();
    const db = databaseRef.current;
    const qs = db ? `?database=${encodeURIComponent(db)}` : "";
    fetch(`${API_BASE_URL}/api/v1/lv/items/${docId}${qs}`, { signal: controller.signal })
      .then((r) => {
        if (!r.ok) throw new Error("LV extraction failed");
        return r.json();
      })
      .then((data) => {
        const items = data.items || [];
        setLvCount(items.length);
        setLvItems(items);
        setDocumentUrl(data.document_url || "");
        setDocumentName(data.document_name || "");
        setPhase("matching");
      })
      .catch((err) => {
        if (err.name === "AbortError") return;
        setError(err.message);
        setPhase("failed");
      });
    return () => controller.abort();
  }, [phase, docId]);

  // Matching effect — fires once when phase becomes "matching".
  // /api/v1/matching/run now runs as a background job, so the POST returns a job
  // envelope ({job_id, status} or {result}); resolve the actual matching report
  // (poll the job when backgrounded) before showing the review.
  useEffect(() => {
    if (phase !== "matching" || !docId) return;
    const catId = catalogIdRef.current;
    if (!catId) {
      setError("No product catalog found. Please upload the Produkthandbuch first.");
      setPhase("failed");
      return;
    }
    const db = databaseRef.current || DEFAULT_DATABASE;
    setMatchStep(4);
    let stop = false;
    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

    const jobResult = (job) => {
      if (job.status === "failed") throw new Error(job.error || "Matching fehlgeschlagen");
      return job.result;
    };
    const pollJob = async (jobId) => {
      const job = await waitFor(jobId);
      if (stop) return null;
      return jobResult(job);
    };

    (async () => {
      try {
        let report;
        const resumed = resumedMatchingRef.current;
        const reattachId = reattachMatchingJobIdRef.current;
        resumedMatchingRef.current = false;
        reattachMatchingJobIdRef.current = null;
        if (resumed) {
          // Resumed run: its matcher was started on an earlier visit. Only
          // re-attach to that job (finished or still running); if it can't be
          // recovered — no saved id, job gone, or ended without a result —
          // say so and let the user start over explicitly. A job that failed
          // surfaces its own error via jobResult.
          if (reattachId) {
            matchingJobIdRef.current = reattachId;
            // A rejected wait means the job is gone or unreachable.
            const job = await waitFor(reattachId).catch(() => null);
            if (stop) return;
            if (job) report = jobResult(job);
          }
          if (report == null) throw new Error(RUN_NOT_RECOVERABLE_MESSAGE);
        } else if (!stop) {
          const res = await fetch(`${API_BASE_URL}/api/v1/matching/run`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ lv_document_id: docId, catalog_document_id: catId, database: db }),
          });
          if (!res.ok) throw new Error("Matching failed");
          const data = await res.json();
          // Resolve the report from any response shape: sync result, background job, or legacy.
          if (data && data.job_id) { matchingJobIdRef.current = data.job_id; report = await pollJob(data.job_id); }
          else if (data && data.result !== undefined) report = data.result;
          else report = data;
        }
        if (stop || report == null) return;
        setMatchStep(5);
        await sleep(STEP5_ADVANCE_DELAY_MS);
        setMatchStep(6);
        await sleep(STEP6_ADVANCE_DELAY_MS);
        if (stop) return;
        setReport(report);
        setPhase("review");
      } catch (err) {
        if (stop) return;
        setError(err.message);
        setPhase("failed");
      }
    })();
    return () => { stop = true; };
  }, [phase, docId, waitFor]);

  // Catalog fetch effect — fires once when phase becomes "review"
  useEffect(() => {
    if (phase !== "review" || !catalogId) return;
    const db = databaseRef.current;
    const qs = db ? `?database=${encodeURIComponent(db)}` : "";
    fetch(`${API_BASE_URL}/api/v1/product-catalog/products/${catalogId}${qs}`)
      .then((r) => {
        if (!r.ok) throw new Error("catalog fetch failed");
        return r.json();
      })
      .then((data) => setCatalogProducts(data.products || []))
      .catch(() => setCatalogProductsError(true));
  }, [phase, catalogId]);

  // ── Event handlers ────────────────────────────────────

  const applyFile = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".pdf")) {
      setError("Only PDF files are allowed");
      return;
    }
    if (f.size > MAX_UPLOAD_BYTES) {
      setError("File too large (max 50 MB)");
      return;
    }
    setFile(f);
    setError(null);
  };

  const handleFileChange = (e) => applyFile(e.target.files?.[0]);

  const handleDrop = (e) => {
    e.preventDefault();
    applyFile(e.dataTransfer.files?.[0]);
  };

  const handleUpload = async () => {
    if (!file) return;
    setPhase("uploading");
    setError(null);
    const formData = new FormData();
    formData.append("files", file);
    const qs = database ? `?database=${encodeURIComponent(database)}` : "";
    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/documents/upload${qs}`, {
        method: "POST",
        body: formData,
      });
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail.detail || "Upload failed");
      }
      const [doc] = await res.json();
      const fname = doc.filename || file.name;
      setDocId(doc.document_id);
      setDocFilename(fname);
      setStatusMsg("In Warteschlange…");
      setPhase("processing"); // persist effect records the resumable state

    } catch (err) {
      setError(err.message);
      setPhase("failed");
    }
  };

  // Export the matching result as CSV and reset. The run itself is already in
  // Recent Runs — the matching job auto-saves on completion (no manual save here).
  const handleExport = async () => {
    if (exportBusy) return;
    setExportBusy(true);
    setError(null);
    try {
      const db = database || DEFAULT_DATABASE;
      const res = await fetch(`${API_BASE_URL}/api/v1/matching/csv`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          lv_document_id: docId,
          catalog_document_id: catalogIdRef.current,
          database: db,
        }),
      });
      if (!res.ok) throw new Error("CSV export failed");
      const data = await res.json();
      let result = data.result;
      if (data.job_id) {
        const job = await waitFor(data.job_id);
        if (job.status === "failed") throw new Error(job.error || "CSV export failed");
        result = job.result;
      }
      const { csv, filename } = result;
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || "matching.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setTimeout(reset, APPROVE_RESET_DELAY_MS);
    } catch (err) {
      setError(err.message);
    } finally {
      setExportBusy(false);
    }
  };

  const handleReject = () => {
    if (window.confirm("Discard this run and start over?")) reset();
  };

  const reset = () => {
    clearActive();
    matchingJobIdRef.current = null;
    reattachMatchingJobIdRef.current = null;
    resumedMatchingRef.current = false;
    setPhase("idle");
    setFile(null);
    setError(null);
    setDocId(null);
    setDocFilename("");
    setStatusMsg("");
    setLvCount(null);
    setReport(null);
    setMatchStep(4);
    setCorrections({});
    setEditingRow(null);
    setDocumentUrl("");
    setDocumentName("");
    setLvItems([]);
    setCatalogProducts([]);
    setCatalogProductsError(false);
  };

  const handleCorrect = (pos, prod) => {
    setCorrections((prev) => ({ ...prev, [pos]: prod }));
    setEditingRow(null);
  };

  // ── Step state calculation ────────────────────────────

  const getStepState = (stepId) => {
    const completed = COMPLETED_THROUGH[phase] ?? 0;
    if (stepId <= completed) return "completed";
    const active = phase === "matching" ? matchStep : ACTIVE_STEP[phase];
    if (stepId === active) return "active";
    return "pending";
  };

  // ── Render ────────────────────────────────────────────

  const showUpload = phase === "idle" || phase === "uploading";
  const showPipeline = phase !== "idle" && phase !== "failed";

  return (
    <section className="lvwf-root" aria-labelledby="lvwf-title">
      <header className="lvwf-section-header">
        <h2 id="lvwf-title" className="ds-section-title ds-flush">WF-02 · LV matching</h2>
        <p className="ds-page-subtitle">
          LV extraction and product matching against the Produkthandbuch · human review mandatory
        </p>
      </header>

      {/* Catalog selector (idle only) */}
      {phase === "idle" && (
        <div className="lvwf-catalog-bar">
          {catalogs.length === 0 && (
            <div className="ds-alert ds-alert--warning ds-flush">
              No Produkthandbuch found in the graph — upload it first.
            </div>
          )}
          {catalogs.length === 1 && (
            <span className="ds-meta">
              Produkthandbuch: <strong className="lvwf-catalog-name">{catalogLabel}</strong>
            </span>
          )}
          {catalogs.length > 1 && (
            <label className="lvwf-catalog-select-label">
              <span className="ds-label">Produkthandbuch</span>
              <select
                className="ds-select ds-select--sm"
                value={catalogId || ""}
                onChange={(e) => {
                  const d = catalogs.find((c) => c.document_id === e.target.value);
                  setCatalogId(e.target.value);
                  catalogIdRef.current = e.target.value;
                  setCatalogLabel(d ? d.filename || d.name : "");
                }}
              >
                <option value="">— select —</option>
                {catalogs.map((c) => (
                  <option key={c.document_id} value={c.document_id}>
                    {c.filename || c.name}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
      )}

      {/* Upload / drag-drop zone */}
      {showUpload && (
        <div
          className={`lvwf-dropzone ${file ? "has-file" : ""}`}
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
        >
          <input
            type="file"
            id="lvwf-input"
            accept=".pdf"
            onChange={handleFileChange}
            disabled={phase === "uploading"}
            className="lvwf-file-input"
          />
          <label htmlFor="lvwf-input" className="lvwf-dropzone-label">
            {phase === "uploading" ? (
              <>
                <LoaderCircle size={ICON_SIZE} className="ds-spin lvwf-loader" aria-hidden="true" />
                <span>Uploading…</span>
              </>
            ) : file ? (
              <span className="lvwf-filename">📄 {file.name}</span>
            ) : (
              <>
                <span className="lvwf-drop-icon" aria-hidden="true">⬆</span>
                <span>
                  Drop LV PDF here or <u>browse</u>
                </span>
                <span className="lvwf-hint">PDF · max 50 MB</span>
              </>
            )}
          </label>
          {file && phase === "idle" && (
            <button
              type="button"
              className="ds-btn ds-btn--primary lvwf-dropzone-action"
              onClick={handleUpload}
              disabled={!catalogId}
            >
              {catalogId ? "Start workflow →" : "Select a catalog first"}
            </button>
          )}
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div className="ds-alert ds-alert--danger ds-flush" role="alert">
          <span>{error}</span>
          <button type="button" onClick={reset} className="ds-btn ds-btn--secondary ds-btn--sm ds-push-end">
            Try again
          </button>
        </div>
      )}

      {/* Live pipeline steps */}
      {showPipeline && (
        <div className="lvwf-pipeline">
          <div className="lvwf-pipeline-hdr">
            <span className="lvwf-live-dot" aria-hidden="true" />
            <h3 className="ds-section-title ds-flush">LV extraction + product matching</h3>
            {docFilename && (
              <span className="lvwf-pipeline-file">{docFilename}</span>
            )}
          </div>
          {phase === "processing" && statusMsg && (
            <div className="lvwf-pipeline-msg" role="status">{statusMsg}</div>
          )}
          {STEPS.map((s) => {
            const st = getStepState(s.id);
            return (
              <div key={s.id} className={`lvwf-step ${st}`}>
                <div className="lvwf-step-num">
                  {st === "completed" ? "✓" : s.id}
                </div>
                <div className="lvwf-step-text">
                  {s.label}
                  {s.id === 3 && lvCount !== null && (
                    <span className="ds-badge ds-badge--success">{lvCount} pos.</span>
                  )}
                </div>
                {st === "active" && (
                  <LoaderCircle size={ICON_SIZE} className="ds-spin lvwf-loader" aria-hidden="true" />
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Raw LV items — shown once extraction completes */}
      {(phase === "matching" || phase === "review") && lvItems.length > 0 && (
        <LVItemsTable
          items={lvItems}
          documentUrl={documentUrl}
          documentName={documentName}
        />
      )}

      {/* Human review table */}
      {phase === "review" && report && (
        <div className="lvwf-review">
          <div className="lvwf-review-hdr">
            <h3 className="ds-section-title ds-flush">Human review</h3>
            <span className="ds-badge ds-badge--warning">Required</span>
            <span className="ds-meta">
              {report.summary.can_supply} matched · {report.summary.missing} missing
            </span>
          </div>

          {catalogProductsError && (
            <div className="ds-alert ds-alert--warning ds-flush">
              Could not load product catalog — product search unavailable.
            </div>
          )}
          <SummaryBar summary={report.summary} />

          <MatchingResults
            report={report}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            corrections={corrections}
            editingRow={editingRow}
            onEditRow={setEditingRow}
            onCorrect={handleCorrect}
            documentUrl={documentUrl}
            documentName={documentName}
            catalogProducts={catalogProducts}
          />

          <div className="lvwf-actions">
            <button type="button" className="ds-btn ds-btn--primary" onClick={handleExport} disabled={exportBusy}>
              {exportBusy ? (
                <>
                  <LoaderCircle size={ICON_SIZE} className="ds-spin" aria-hidden="true" />
                  Exporting…
                </>
              ) : "⬇ Export CSV"}
            </button>
            <button
              type="button"
              className="ds-btn ds-btn--secondary"
              onClick={() => setEditingRow(null)}
            >
              ✎ Correct
            </button>
            <button type="button" className="ds-btn ds-btn--danger" onClick={handleReject}>
              ✕ Reject
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
